package com.wintermar.inspection.navigation.feature

import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.lifecycle.viewmodel.navigation3.rememberViewModelStoreNavEntryDecorator
import androidx.navigation3.runtime.NavKey
import androidx.navigation3.runtime.entryProvider
import androidx.navigation3.runtime.rememberNavBackStack
import androidx.navigation3.runtime.rememberSaveableStateHolderNavEntryDecorator
import androidx.navigation3.ui.NavDisplay
import androidx.savedstate.serialization.SavedStateConfiguration
import com.wintermar.inspection.feature.domain.model.inspection.InspectionModel
import com.wintermar.inspection.feature.domain.model.template.InspectionTemplateModel
import com.wintermar.inspection.feature.domain.model.template.TemplateOptionModel
import com.wintermar.inspection.feature.presentation.inspection.answer.InspectionAnswerScreen
import com.wintermar.inspection.feature.presentation.inspection.answer.search.InspectionSearchQuestionScreen
import com.wintermar.inspection.feature.presentation.inspection.complete.InspectionCompleteScreen
import com.wintermar.inspection.feature.presentation.inspection.complete_answer.CompleteAnswerScreen
import com.wintermar.inspection.feature.presentation.inspection.landing.InspectionLandingScreen
import com.wintermar.inspection.navigation.Route
import kotlinx.coroutines.delay
import kotlinx.serialization.modules.SerializersModule
import kotlinx.serialization.modules.polymorphic
import kotlin.time.Duration.Companion.milliseconds

@Composable
fun InspectionNavigation(
    paddingValues: PaddingValues,
    initialBackStack: Route? = null,
    onVisibilityChanged: (showTopBar: Boolean) -> Unit,
    onResetInitialBackStack: () -> Unit
) {
    val inspectionBackStack = rememberNavBackStack(
        configuration = SavedStateConfiguration {
            serializersModule = SerializersModule {
                polymorphic(NavKey::class) {
                    subclass(Route.Inspection.List::class, Route.Inspection.List.serializer())
                    subclass(
                        Route.Inspection.Answer::class,
                        Route.Inspection.Answer.serializer()
                    )
                    subclass(
                        Route.Inspection.SearchQuestion::class,
                        Route.Inspection.SearchQuestion.serializer()
                    )
                    subclass(
                        Route.Inspection.CompleteAnswer::class,
                        Route.Inspection.CompleteAnswer.serializer()
                    )
                    subclass(
                        Route.Inspection.Complete::class,
                        Route.Inspection.Complete.serializer()
                    )
                }
            }
        },
        Route.Inspection.List
    )

    LaunchedEffect(initialBackStack) {
        if (initialBackStack != null) {
            delay(300.milliseconds)
            inspectionBackStack.add(initialBackStack)
            onResetInitialBackStack()
        }
    }

    val currentChild = inspectionBackStack.lastOrNull() as? Route
    LaunchedEffect(currentChild) {
        onVisibilityChanged(currentChild?.showTopBar == true)
    }

    NavDisplay(
        backStack = inspectionBackStack,
        transitionSpec = {
            slideInVertically { it } + fadeIn() togetherWith
                    slideOutVertically { -it } + fadeOut()
        },
        popTransitionSpec = {
            slideInVertically { -it } + fadeIn() togetherWith
                    slideOutVertically { it } + fadeOut()
        },
        predictivePopTransitionSpec = {
            slideInVertically { -it } + fadeIn() togetherWith
                    slideOutVertically { it } + fadeOut()
        },
        entryDecorators = listOf(
            rememberSaveableStateHolderNavEntryDecorator(),
            rememberViewModelStoreNavEntryDecorator()
        ),
        entryProvider = entryProvider {
            var updatedProgressInspection by remember { mutableStateOf(InspectionModel()) }
            var showSuccessUpdateToast by remember { mutableStateOf(false) }
            var inspectionModel by remember { mutableStateOf(InspectionModel()) }
            var templateOptionModel by remember { mutableStateOf(listOf<TemplateOptionModel>()) }
            var excludeInspectionId by remember { mutableStateOf("") }
            var inspectionTemplate: InspectionTemplateModel? by remember { mutableStateOf(null) }
            var searchResultPageId: String? by remember { mutableStateOf(null) }
            var searchResultSectionId: String? by remember { mutableStateOf(null) }
            var searchResultQuestionId: String? by remember { mutableStateOf(null) }

            fun reset() {
                excludeInspectionId = ""
                showSuccessUpdateToast = false
                updatedProgressInspection = InspectionModel()
                inspectionTemplate = null
                searchResultPageId = null
                searchResultSectionId = null
                searchResultQuestionId = null
            }

            entry<Route.Inspection.List> {
                InspectionLandingScreen(
                    paddingValues = paddingValues,
                    updatedProgressInspection = updatedProgressInspection,
                    showSuccessUpdateToast = showSuccessUpdateToast,
                    excludeInspectionId = excludeInspectionId,
                    onGoToInspection = {
                        inspectionBackStack.add(Route.Inspection.Answer(it))
                    },
                    onCompleted = { data, templateOptions ->
                        inspectionModel = data
                        templateOptionModel = templateOptions
                        inspectionBackStack.add(Route.Inspection.Complete)
                    }
                )
            }

            entry<Route.Inspection.Answer> { entry ->
                InspectionAnswerScreen(
                    inspectionId = entry.inspectionId,
                    paddingValues = paddingValues,
                    searchResultData = inspectionTemplate,
                    searchSelectedResult = Triple(
                        searchResultPageId,
                        searchResultSectionId,
                        searchResultQuestionId
                    ),
                    onClose = { inspectionModel ->
                        showSuccessUpdateToast = true
                        updatedProgressInspection = inspectionModel
                        inspectionBackStack.remove(Route.Inspection.Answer(entry.inspectionId))
                    },
                    toCompleteScreen = {
                        showSuccessUpdateToast = false
                        inspectionBackStack.add(Route.Inspection.CompleteAnswer(it))
                        inspectionBackStack.remove(Route.Inspection.Answer(entry.inspectionId))
                        updatedProgressInspection = InspectionModel(
                            id = it.inspectionId,
                            lastUpdated = it.lastUpdated,
                            vesselName = it.vesselName,
                            totalAnswer = it.totalAnswer,
                            totalQuestion = it.totalQuestion,
                            status = it.inspectionStatus
                        )
                    },
                    toSearchQuestion = {
                        inspectionTemplate = it
                        inspectionBackStack.add(Route.Inspection.SearchQuestion)
                    }
                )
            }

            entry<Route.Inspection.SearchQuestion> {
                InspectionSearchQuestionScreen(
                    inspectionTemplate = inspectionTemplate ?: InspectionTemplateModel(),
                    paddingValues = paddingValues,
                    onClose = { inspectionData, selectedPageId, selectedSectionId, selectedQuestionId ->
                        inspectionTemplate = inspectionData
                        searchResultPageId = selectedPageId
                        searchResultSectionId = selectedSectionId
                        searchResultQuestionId = selectedQuestionId
                        inspectionBackStack.remove(Route.Inspection.SearchQuestion)
                    }
                )
            }

            entry<Route.Inspection.CompleteAnswer> { entry ->
                CompleteAnswerScreen(
                    summaryData = entry.summaryData,
                    onDone = {
                        inspectionBackStack.remove(Route.Inspection.CompleteAnswer(entry.summaryData))
                    }
                )
            }

            entry<Route.Inspection.Complete> {
                InspectionCompleteScreen(
                    paddingValues = paddingValues,
                    data = inspectionModel,
                    templateOptions = templateOptionModel,
                    onBack = {
                        reset()
                        inspectionBackStack.remove(Route.Inspection.Complete)
                    },
                    onEdit = {
                        reset()
                        inspectionBackStack.remove(Route.Inspection.Complete)
                        inspectionBackStack.add(Route.Inspection.Answer(inspectionModel.id))
                    },
                    onApproved = { inspectionId ->
                        reset()
                        inspectionBackStack.remove(Route.Inspection.Complete)
                        excludeInspectionId = inspectionId
                    }
                )
            }
        }
    )
}
